import { z } from 'zod';
import { centavos } from './envelope.ts';

/**
 * The Casino Back Office dashboard.
 *
 * "Range control drives every widget on the screen; the comparison series is
 * always the immediately prior period of equal length."
 * "Brand filter is a global scope, not a per-widget filter."
 */

export const dateRange = z.enum(['TODAY', 'SEVEN_DAYS', 'THIRTY_DAYS', 'QTD', 'CUSTOM']);
export type DateRange = z.infer<typeof dateRange>;

export const dashboardQuery = z
  .object({
    range: dateRange.default('THIRTY_DAYS'),
    /** Omitted means all brands. */
    brandId: z.string().optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
  })
  .refine((q) => q.range !== 'CUSTOM' || (q.from && q.to), {
    message: 'A CUSTOM range needs both `from` and `to`.',
    path: ['from'],
  });
export type DashboardQuery = z.infer<typeof dashboardQuery>;

/** A figure with its comparison against the immediately prior period. */
export const metric = z.object({
  value: centavos,
  previous: centavos,
  /** Basis points. +1240 is "▲ 12.4%". Null when the prior period was zero. */
  deltaBp: z.number().int().nullable(),
});

export const countMetric = z.object({
  value: z.number().int().nonnegative(),
  previous: z.number().int().nonnegative(),
  deltaBp: z.number().int().nullable(),
});

export const dashboardSummary = z.object({
  range: z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    comparedFrom: z.iso.datetime(),
    comparedTo: z.iso.datetime(),
  }),
  ggr: metric,
  deposits: metric,
  withdrawals: metric,
  activePlayers: countMetric,
  /** GGR ÷ total bets, in basis points. The "Hold 4.8%" tile. */
  holdBp: z.number().int().nullable(),
  /** GGR ÷ active players, in centavos. */
  arpu: centavos,
});
export type DashboardSummary = z.infer<typeof dashboardSummary>;

/** The action queue band. Every count is live, not cached. */
export const actionQueue = z.object({
  withdrawalsAwaitingChecker: z.object({
    count: z.number().int().nonnegative(),
    value: centavos,
    oldestMinutes: z.number().int().nonnegative().nullable(),
    breachingSla: z.number().int().nonnegative(),
  }),
  /**
   * Identity checks waiting for a person: CURRENT identity records whose outcome
   * is REVIEW, by the definition the identity queue lists its cases by, in the
   * reader's brand scope and — like every count here — narrowed by the brand
   * chosen in the header, which the identity queue follows too: the count is the
   * number of cases that list holds (it shows the oldest 200).
   * `oldestMinutes` is the oldest one's wait, measured from its `recorded_at` —
   * the instant the queue counts its days from, not when the player sent the
   * photographs — and null when nothing is waiting.
   */
  kycReview: z.object({
    count: z.number().int().nonnegative(),
    oldestMinutes: z.number().int().nonnegative().nullable(),
  }),
  riskAlerts: z.object({
    count: z.number().int().nonnegative(),
    critical: z.number().int().nonnegative(),
  }),
  supportTickets: z.object({
    count: z.number().int().nonnegative(),
    escalated: z.number().int().nonnegative(),
  }),
});
export type ActionQueue = z.infer<typeof actionQueue>;

export const trendPoint = z.object({
  day: z.iso.date(),
  ggr: centavos,
  deposits: centavos,
  withdrawals: centavos,
  activePlayers: z.number().int().nonnegative(),
});
export type TrendPoint = z.infer<typeof trendPoint>;

export const brandMixRow = z.object({
  brandId: z.string(),
  brandName: z.string(),
  ggr: centavos,
  /** Basis points of the total. */
  shareBp: z.number().int().nonnegative(),
});

export const topGameRow = z.object({
  gameId: z.string(),
  name: z.string(),
  provider: z.string(),
  ggr: centavos,
  holdBp: z.number().int().nullable(),
  rounds: z.number().int().nonnegative(),
});

export const topPlayerRow = z.object({
  playerId: z.string(),
  ref: z.string(),
  name: z.string(),
  brandName: z.string(),
  agentRef: z.string().nullable(),
  tier: z.string(),
  deposits: centavos,
  withdrawals: centavos,
  netDeposits: centavos,
  ggr: centavos,
});

/**
 * A brand licence inside the 60-day advance-warning window, or already lapsed.
 *
 * "Licence expiry drives a 60-day advance warning on the dashboard." Only the
 * brands actually inside the window appear, so an empty array is the calm state
 * and the dashboard draws nothing at all.
 */
export const licenceWarning = z.object({
  brandId: z.string(),
  brandName: z.string(),
  /** The regulator's reference, e.g. PAGCOR-2026-0417. Null when none recorded. */
  licenceRef: z.string().nullable(),
  expiresAt: z.iso.datetime(),
  /** Already past. Not a warning any more — a brand trading without a licence. */
  expired: z.boolean(),
});
export type LicenceWarning = z.infer<typeof licenceWarning>;

export const dashboardResponse = z.object({
  summary: dashboardSummary,
  queue: actionQueue,
  trend: z.array(trendPoint),
  brandMix: z.array(brandMixRow),
  topGames: z.array(topGameRow),
  topPlayers: z.array(topPlayerRow),
  licences: z.array(licenceWarning),
  /**
   * Sections the platform cannot answer yet, named rather than zeroed.
   * A "₱0" that means "not built" is a number an operator will act on.
   */
  unavailable: z.array(z.object({ section: z.string(), reason: z.string() })),
});
export type DashboardResponse = z.infer<typeof dashboardResponse>;
